<template>
  <div class="payment-container">
    <!-- Хедер с информацией о покупке -->
    <div class="payment-header">
      <div class="header-icon">💰</div>
      <div class="header-content">
        <h1 class="header-title">Оплата заказа</h1>
        <div class="header-subtitle">Выберите способ оплаты</div>
      </div>
    </div>

    <!-- Информация о заказе -->
    <div class="order-summary-card">
      <div class="summary-header">
        <span class="summary-icon">📦</span>
        <h3>Ваш заказ</h3>
      </div>
      <div class="summary-content">
        <div class="summary-item">
          <span class="item-label">Товар:</span>
          <span class="item-value">{{ productName }}</span>
        </div>
        <div class="summary-item">
          <span class="item-label">Размер:</span>
          <span class="item-value">{{ selectedSize }}</span>
        </div>
        <div class="summary-item">
          <span class="item-label">Сумма:</span>
          <span class="item-value">{{ formatPrice(paymentAmount) }} ₽</span>
        </div>
        <div v-if="deliveryService" class="summary-item">
          <span class="item-label">Доставка:</span>
          <span class="item-value">{{ deliveryService }}</span>
        </div>
      </div>
    </div>

    <!-- Главная сумма к оплате -->
    <div class="amount-card">
      <div class="amount-label">Сумма к оплате</div>
      <div class="amount-value">{{ formatPrice(paymentAmount) }} ₽</div>
    </div>

    <!-- Выбор способа оплаты -->
    <div class="payment-method-card">
      <div class="card-header">
        <span class="header-icon">💳</span>
        <h3>Способ оплаты</h3>
      </div>
      
      <div class="payment-options-grid">
        <!-- Оплата с баланса (только для дроп-пользователей) -->
        <div 
          v-if="isDrop && !isFromAbroad"
          class="payment-option"
          :class="{ 'selected': paymentMethod === 'balance' }"
          @click="selectPaymentMethod('balance')"
        >
          <div class="option-icon">
            <div class="icon-circle balance-icon">💎</div>
          </div>
          <div class="option-content">
            <div class="option-title">
              Оплатить с баланса
              <span class="option-badge" v-if="paymentMethod === 'balance'">Выбрано</span>
            </div>
            <div class="option-subtitle">Доступно: {{ formatPrice(userBalance) }} ₽</div>
            
            <!-- Балансовая информация -->
            <div v-if="paymentMethod === 'balance'" class="balance-details">
              <div class="balance-row">
                <span>Сумма списания:</span>
                <span class="amount">{{ formatPrice(paymentAmount) }} ₽</span>
              </div>
              <div class="balance-row">
                <span>Баланс после:</span>
                <span class="amount" :class="{
                  'positive': (userBalance - paymentAmount) > 0,
                  'zero': (userBalance - paymentAmount) <= 0
                }">
                  {{ formatPrice(userBalance - paymentAmount) }} ₽
                </span>
              </div>
              
              <!-- Индикатор недостатка средств -->
              <div 
                v-if="userBalance < paymentAmount && paymentMethod === 'balance'" 
                class="insufficient-funds"
              >
                <span class="warning-icon">⚠️</span>
                <span>Недостаточно средств</span>
              </div>
            </div>
          </div>
          <div class="option-radio">
            <div class="radio-circle" :class="{ 'selected': paymentMethod === 'balance' }"></div>
          </div>
        </div>

        <!-- Оплата картой -->
        <div 
          class="payment-option"
          :class="{ 'selected': paymentMethod === 'card' }"
          @click="selectPaymentMethod('card')"
        >
          <div class="option-icon">
            <div class="icon-circle card-icon">💳</div>
          </div>
          <div class="option-content">
            <div class="option-title">
              Оплата на карту
              <span class="option-badge" v-if="paymentMethod === 'card'">Выбрано</span>
            </div>
          </div>
          <div class="option-radio">
            <div class="radio-circle" :class="{ 'selected': paymentMethod === 'card' }"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Банковская карта (только при выборе оплаты на карту) -->
    <div v-if="paymentMethod === 'card'" class="bank-card-section">
      <div class="section-header">
        <span class="section-icon">🏦</span>
        <h3>Реквизиты для оплаты</h3>
      </div>
      
      <div class="bank-card">
        <div class="card-background"></div>
        <div class="card-content">
          <div class="card-header">
            <div class="card-bank-large">{{ cardData.bank_name }}</div>
            <div class="card-chip">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                <rect x="2" y="4" width="20" height="16" rx="2" ry="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
                <line x1="2" y1="14" x2="22" y2="14"></line>
              </svg>
            </div>
          </div>
          
          <div class="card-number-section">
            <div class="card-number-display">{{ formatCardNumber(cardData.card_number) }}</div>
            <button class="copy-card-btn" @click="copyCardNumber">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          </div>
          
          <div class="card-footer">
            <div class="card-owner">
              <div class="owner-label">Владелец карты</div>
              <div class="owner-name">{{ cardData.card_owner }}</div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="card-instructions">
        <div class="instruction-item">
          <span class="instruction-icon">📱</span>
          <span>Переведите точную сумму на эту карту</span>
        </div>
        <div class="instruction-item">
          <span class="instruction-icon">⏱️</span>
          <span>Оплата обрабатывается в течение нескольких часов</span>
        </div>
      </div>
    </div>

    <!-- Загрузка чека (только для оплаты на карту) -->
    <div v-if="paymentMethod === 'card'" class="receipt-upload-section">
      <div class="section-header">
        <span class="section-icon">📄</span>
        <h3>Чек об оплате</h3>
        <span class="required-badge">Обязательно</span>
      </div>
      
      <div 
        class="upload-area"
        :class="{
          'has-file': fileName,
          'dragover': isDragging
        }"
        @click="triggerFileInput"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div class="upload-icon">
          <svg v-if="!fileName" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <svg v-else width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="9" y1="15" x2="15" y2="15"></line>
          </svg>
        </div>
        
        <div class="upload-content">
          <div class="upload-title" v-if="!fileName">
            Прикрепите чек об оплате
          </div>
          <div class="upload-title" v-else>
            {{ fileName }}
          </div>
          
          <div class="upload-subtitle" v-if="!fileName">
            Нажмите для выбора файла
          </div>
          <div class="upload-subtitle" v-else>
            Чек успешно загружен
          </div>
          
          <div class="upload-hint">
            Только формат PDF
          </div>
        </div>
        
        <div v-if="fileName" class="upload-actions">
          <button class="remove-file-btn" @click.stop="removeFile">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      
      <input
        type="file"
        accept="application/pdf"
        ref="fileInput"
        @change="onFileChange"
        style="display: none;"
      />
    </div>

    <!-- Информация для пользователя -->
    <div class="user-info">
      <div class="info-item">
        <span class="info-icon">📝</span>
        <span class="info-text">После оплаты нажмите кнопку "Завершить оформление" внизу экрана</span>
      </div>
    </div>

    <!-- Простая анимация успешной оплаты -->
    <div v-if="showSuccessAnimation" class="simple-success-overlay">
      <div class="simple-animation-container">
        <!-- Зеленая галочка по центру круга -->
        <div class="success-icon-container">
          <div class="circle-outline">
            <div class="checkmark" :class="{ 'show': showCheckmark }">
              <svg width="50" height="50" viewBox="0 0 50 50" fill="none">
                <path 
                  d="M15 25L22 32L35 19" 
                  stroke="#10B981" 
                  stroke-width="4" 
                  stroke-linecap="round" 
                  stroke-linejoin="round"
                  stroke-dasharray="40"
                  :stroke-dashoffset="checkmarkOffset"
                />
              </svg>
            </div>
          </div>
        </div>
        
        <!-- Текст успеха -->
        <div class="success-text">
          <h2 class="success-title" :class="{ 'fade-in': showTitle }">Оплата прошла успешно</h2>
          <div class="success-subtitle" :class="{ 'fade-in': showSubtitle }">
            {{ paymentMethod === 'balance' ? 'Средства списаны с баланса' : 'Оплата получена' }}
          </div>
        </div>
        
        <!-- Информация о заказе -->
        <div class="order-info-card" :class="{ 'slide-up': showOrderInfo }">
          <div class="info-section">
            <h3 class="info-title">Детали заказа</h3>
            <div class="info-grid">
              <div class="info-row">
                <span class="info-label">Товар:</span>
                <span class="info-value">{{ productName }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Размер:</span>
                <span class="info-value">{{ selectedSize }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Сумма:</span>
                <span class="info-value highlight">{{ formatPrice(paymentAmount) }} ₽</span>
              </div>
              <div v-if="paymentMethod === 'balance'" class="info-row">
                <span class="info-label">Новый баланс:</span>
                <span class="info-value">{{ formatPrice(userBalance - paymentAmount) }} ₽</span>
              </div>
              <div v-if="orderNumber" class="info-row">
                <span class="info-label">Номер заказа:</span>
                <span class="info-value">#{{ orderNumber }}</span>
              </div>
            </div>
          </div>
          
          <div class="status-section">
            <div class="status-badge">
              <span class="status-dot"></span>
              <span class="status-text">Заказ создан</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/api';

export default {
  data() {
    return {
      fileName: "",
      fileData: null,
      isDragging: false,
      isProcessing: false,
      cardData: {'bank_name': 'ВТБ', 'card_number': '2200 2480 7219 6917', 'card_owner': 'Анна З.'},
      paymentMethod: 'card',
      userBalance: 0,
      profileId: null,
      
      // Анимационные состояния
      showSuccessAnimation: false,
      showCheckmark: false,
      showTitle: false,
      showSubtitle: false,
      showOrderInfo: false,
      checkmarkOffset: 40,
      animationComplete: false,
      animationInProgress: false
    };
  },
  props: {
    paymentAmount: { type: Number, required: true },
    selectedSize: { type: String, required: true },
    productName: { type: String, required: true },
    productPhoto: { type: String, required: true },
    isDrop: { type: Boolean, default: false },
    deliveryService: { type: String },
    orderNumber: { type: String },
    trackNumber: { type: String },
    baracodePhoto: { type: String },
    orderLink: { type: String },
    isFromAbroad: { type: Boolean, default: false },
    batchId: { type: [Number, String], default: null },
  },
  computed: {
    // Проверяем, можно ли завершить оплату (для активации кнопки "Завершить оформление")
    canCompletePayment() {
      if (this.isProcessing || this.animationInProgress) return false;
      
      if (!this.isFromAbroad && this.isDrop && this.paymentMethod === 'balance') {
        return this.userBalance >= this.paymentAmount;
      }
      
      if (this.paymentMethod === 'card') {
        return !!this.fileName && !!this.fileData;
      }
      
      return false;
    }
  },
  async mounted() {
    console.log("🔄 Component mounted, isDrop:", this.isDrop);
    
    if (window.Telegram && window.Telegram.WebApp) {
      const tg = window.Telegram.WebApp;
      
      tg.MainButton.hide();
      
      tg.MainButton.setParams({
        text: "ЗАВЕРШИТЬ",
        color: "#2AABEE",
        text_color: "#FFFFFF"
      });
      
      this.$watch(
        () => this.canCompletePayment,
        (canComplete) => {
          if (this.isProcessing || this.animationInProgress || this.animationComplete) return;

          if (canComplete) {
            tg.MainButton.show();
            tg.MainButton.enable();
          } else {
            tg.MainButton.disable();
          }
        },
        { immediate: true }
      );
      
      tg.MainButton.onClick(this.sendOrder);
    }

    this.getProfileIdFromStorage();
    await this.fetchCardData();
    
    if (this.isDrop) {
      await this.fetchUserBalance();
      if ( !this.isFromAbroad && this.userBalance >= this.paymentAmount) {
        this.paymentMethod = 'balance';
      }
    }
  },
  beforeUnmount() {
    if (window.Telegram && window.Telegram.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.MainButton.offClick(this.sendOrder);
      tg.MainButton.disable();
      tg.MainButton.hide();
    }
  },
  methods: {

    // Получаем Telegram ID из localStorage, с поддержкой разных ключей для надежности
    getProfileIdFromStorage() {
      try {
        const tgId = localStorage.getItem('tg_id') || 
                     localStorage.getItem('telegram_id') || 
                     localStorage.getItem('user_id') ||
                     localStorage.getItem('profile_id');
        
        if (tgId) {
          this.profileId = parseInt(tgId);
          console.log("✅ Telegram ID from localStorage:", this.profileId);
        } else {
          console.log("❌ Telegram ID not found in localStorage");
          this.profileId = 123456;
        }
      } catch (error) {
        console.error("❌ Error reading from localStorage:", error);
        this.profileId = 123456;
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    // Изменение файла через input
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.type !== 'application/pdf') {
          this.showNotification("Пожалуйста, выберите файл в формате PDF", '#EF476F');
          return;
        }
        
        if (file.size > 10 * 1024 * 1024) {
          this.showNotification("Файл слишком большой. Максимальный размер: 10MB", '#EF476F');
          return;
        }
        
        this.fileName = file.name;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.fileData = e.target.result.split(',')[1];
        };
        reader.readAsDataURL(file);
      }
    },

    // Удаление прикрепленного файла
    removeFile() {
      this.fileName = "";
      this.fileData = null;
      this.$refs.fileInput.value = "";
    },

    // Обработка события dragover для отображения эффекта перетаскивания
    onDragOver() {
      this.isDragging = true;
    },

    // Обработка события dragleave для отмены эффекта перетаскивания
    onDragLeave() {
      this.isDragging = false;
    },

    // Обработка события drop для загрузки файла перетаскиванием
    onDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type === 'application/pdf') {
        if (file.size > 10 * 1024 * 1024) {
          this.showNotification("Файл слишком большой. Максимальный размер: 10MB", '#EF476F');
          return;
        }
        
        this.fileName = file.name;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.fileData = e.target.result.split(',')[1];
        };
        reader.readAsDataURL(file);
      } else {
        this.showNotification("Пожалуйста, перетащите файл в формате PDF", '#EF476F');
      }
    },

    // Копирование номера карты в буфер обмена
    copyCardNumber() {
      navigator.clipboard.writeText(this.cardData.card_number)
        .then(() => {
          this.showNotification('Номер карты скопирован!', '#06D6A0');
        })
        .catch(err => {
          console.error("Ошибка при копировании: ", err);
          this.showNotification('Ошибка копирования', '#EF476F');
        });
    },

    // Универсальная функция для отображения уведомлений (использует Telegram WebApp, если доступно, или кастомные уведомления)
    showNotification(message, color) {
      if (window.Telegram && window.Telegram.WebApp) {
        window.Telegram.WebApp.showPopup({
          title: message,
          message: '',
          buttons: [{ type: 'close' }]
        });
      } else {
        const notification = document.createElement('div');
        notification.className = 'copy-notification';
        notification.textContent = message;
        notification.style.background = color;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.classList.add('show');
        }, 10);
        
        setTimeout(() => {
          notification.classList.remove('show');
          setTimeout(() => {
            document.body.removeChild(notification);
          }, 300);
        }, 2000);
      }
    },

    // Форматирование цены с разделителями тысяч
    formatPrice(price) {
      return new Intl.NumberFormat('ru-RU').format(price);
    },

    // Форматирование номера карты с пробелами каждые 4 цифры
    formatCardNumber(number) {
      return number.replace(/(\d{4})/g, '$1 ').trim();
    },

    // Получение данных карты с сервера
    async fetchCardData() {
      try {
        console.log("🔄 Fetching card data...");
        const response = await api.get("/orders/card/");
        console.log("✅ Card data fetched:", response.data);
        if (response.data && response.data.card_number) {
          this.cardData = response.data;
        }
      } catch (error) {
        console.error("❌ Ошибка получения данных карты:", error);
      }
    },

    // Получение баланса пользователя для отображения и проверки при оплате с баланса
    async fetchUserBalance() {
      try {
        console.log("🔄 Starting balance fetch...");
        
        if (!this.profileId) {
          console.error("❌ Profile ID not available");
          return;
        }

        const response = await api.get(`/drop/profile/balance/${this.profileId}`);
        
        if (response.data && response.data.user_balance !== undefined) {
          this.userBalance = response.data.user_balance;
        }
      } catch (error) {
        console.error("❌ Ошибка получения баланса:", error);
      }
    },

    // Выбор способа оплаты и проверка баланса при выборе оплаты с баланса
    selectPaymentMethod(method) {
      if (method === 'balance' && this.userBalance < this.paymentAmount) {
        this.showNotification(
          `Недостаточно средств на балансе. Доступно: ${this.formatPrice(this.userBalance)} ₽`,
          '#EF476F'
        );
        return;
      }
      
      this.paymentMethod = method;
      console.log("Выбран способ оплаты:", method);
      
      if (method === 'balance') {
        this.removeFile();
      }
    },
    
    // Простая анимация успеха
    startSimpleAnimation() {
      this.animationInProgress = true;
      this.showSuccessAnimation = true;
      
      // Скрываем кнопку Telegram WebApp
      if (window.Telegram && window.Telegram.WebApp) {
        window.Telegram.WebApp.MainButton.disable();
        window.Telegram.WebApp.MainButton.hide();
      }
      
      // Последовательность анимаций (2.5 секунды)
      setTimeout(() => {
        this.showCheckmark = true;
        this.animateCheckmark();
      }, 300);
      
      setTimeout(() => {
        this.showTitle = true;
      }, 800);
      
      setTimeout(() => {
        this.showSubtitle = true;
      }, 1100);
      
      setTimeout(() => {
        this.showOrderInfo = true;
      }, 1400);
      
      setTimeout(() => {
        this.animationComplete = true;
        this.animationInProgress = false;
        
        // Автоматическое закрытие через 3 секунды
        setTimeout(() => {
          this.closeAnimation();
        }, 3000);
      }, 1700);
    },
    
    // Анимация рисования галочки
    animateCheckmark() {
      let offset = 40;
      const interval = setInterval(() => {
        offset -= 2;
        this.checkmarkOffset = offset;
        if (offset <= 0) {
          clearInterval(interval);
          this.checkmarkOffset = 0;
        }
      }, 16); // 320ms для анимации
    },
    
    // Закрытие анимации и сброс состояний
    closeAnimation() {
      if (!this.animationComplete) return;
      
      // Анимация закрытия
      this.animationInProgress = true;
      
      // Исчезающие анимации
      this.showOrderInfo = false;
      this.showSubtitle = false;
      this.showTitle = false;
      this.showCheckmark = false;
      
      setTimeout(() => {
        this.showSuccessAnimation = false;
        
        // Сброс всех состояний
        this.checkmarkOffset = 40;
        this.animationComplete = false;
        this.animationInProgress = false;
      }, 500);
    },
    
    // Отправка данных заказа на сервер и обработка ответа, с учетом выбранного способа оплаты и загрузки файла при оплате на карту
    // async sendOrder() {
    //   if (!this.canCompletePayment || this.animationInProgress) return;
      
    //   this.isProcessing = true;
      
    //   if (window.Telegram?.WebApp) {
    //     const tg = window.Telegram.WebApp;
    //     tg.MainButton.hide();
    //     tg.MainButton.disable();
    //     tg.MainButton.showProgress(false);
    //   }
      
    //   console.log("🔄 Starting order submission...");
      
    //   // Показываем анимацию
    //   this.startSimpleAnimation();
      
    //   let fileUrl = '';

    //   // Загружаем файл только если оплата на карту
    //   if (this.paymentMethod === 'card') {
    //     try {
    //       const byteCharacters = atob(this.fileData);
    //       const byteNumbers = new Array(byteCharacters.length);
    //       for (let i = 0; i < byteCharacters.length; i++) {
    //         byteNumbers[i] = byteCharacters.charCodeAt(i);
    //       }
    //       const byteArray = new Uint8Array(byteNumbers);
    //       const fileBlob = new Blob([byteArray], { type: "application/pdf" });

    //       const formData = new FormData();
    //       formData.append("file", fileBlob, this.fileName);

    //       const response = await api.post("/files/upload-file/", formData);
    //       fileUrl = response.data.file_url;
    //       console.log("File uploaded, URL:", fileUrl);

    //     } catch (e) {
    //       console.error("Ошибка загрузки файла:", e);
    //       this.showNotification("Ошибка загрузки файла", '#EF476F');
    //       this.isProcessing = false;
    //       this.closeAnimation();
          
    //       if (window.Telegram && window.Telegram.WebApp) {
    //         window.Telegram.WebApp.MainButton.hide();
    //         window.Telegram.WebApp.MainButton.hideProgress();
    //       }
    //       return;
    //     }
    //   }

    //   try {
    //     const orderRequest = {
    //       profile_id: this.profileId,
    //       product_name: this.productName,
    //       selected_size: this.selectedSize,
    //       payment_amount: this.paymentAmount,
    //       payment_method: this.paymentMethod,
    //       user_balance: this.userBalance,
    //       delivery_service: this.deliveryService,
    //       order_number: this.orderNumber,
    //       track_number: this.trackNumber,
    //       order_link: this.orderLink,
    //       baracode_photo: this.baracodePhoto,
    //       product_photo: this.productPhoto,
    //       file_name: this.fileName,
    //       file_url: fileUrl,
    //       ...(this.paymentMethod === 'card' && {
    //         card_number: this.cardData.card_number,
    //         card_owner: this.cardData.card_owner,
    //         bank_name: this.cardData.bank_name,
    //       })
    //     };

    //     console.log("📦 Sending order data to backend:", orderRequest);

    //     if (this.isDrop) {
    //       const response = await api.post("/drop/profile/order/create", orderRequest);
    //       console.log("✅ Order creation response:", response.data);

    //       // Ждем завершения анимации перед закрытием
    //       setTimeout(() => {
    //         if (window.Telegram && window.Telegram.WebApp) {
              
    //           setTimeout(() => {
    //             if (window.Telegram && window.Telegram.WebApp) {
    //               window.Telegram.WebApp.MainButton.disable();
    //               window.Telegram.WebApp.MainButton.hide();
    //               window.Telegram.WebApp.MainButton.offClick(this.sendOrder);
    //               window.Telegram.WebApp.close();
    //             }
    //           }, 1000);
    //         }
    //       }, 3000);

    //     } else {
    //       const orderData = {
    //         productName: this.productName,
    //         selectedSize: this.selectedSize,
    //         paymentAmount: this.paymentAmount,
    //         fileName: this.fileName,
    //         fileUrl: fileUrl,
    //         productPhoto: this.productPhoto,
    //         isDrop: this.isDrop,
    //         paymentMethod: this.paymentMethod,
    //         userBalance: this.userBalance,
    //         profileId: this.profileId,
    //         deliveryService: this.deliveryService,
    //         orderNumber: this.orderNumber,
    //         trackNumber: this.trackNumber,
    //         baracodePhoto: this.baracodePhoto,
    //         orderLink: this.orderLink,
    //         ...(this.paymentMethod === 'card' && {
    //           cardNumber: this.cardData.card_number,
    //           cardOwner: this.cardData.card_owner,
    //           bankName: this.cardData.bank_name,
    //         })
    //       };

    //       console.log("📦 Order data for regular user:", orderData);

    //       // Ждем завершения анимации перед закрытием
    //       setTimeout(() => {
    //         if (window.Telegram && window.Telegram.WebApp) {
    //           window.Telegram.WebApp.sendData(JSON.stringify(orderData));
    //           window.Telegram.WebApp.close();
    //         }
    //       }, 3000);
    //     }

    //   } catch (error) {
    //     console.error("❌ Error creating order:", error);
    //     const errorMessage = error.response?.data?.detail || error.message;
    //     this.showNotification("Ошибка: " + errorMessage, '#EF476F');
    //     this.closeAnimation();
    //   } finally {
    //     this.isProcessing = false;
        
    //     if (window.Telegram && window.Telegram.WebApp) {
    //       window.Telegram.WebApp.MainButton.hideProgress();
    //     }
    //   }
    // },
    
    async sendOrder() {
      if (!this.canCompletePayment || this.animationInProgress) return;
      
      this.isProcessing = true;
      
      if (window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        tg.MainButton.hide();
        tg.MainButton.disable();
        tg.MainButton.showProgress(false);
      }
      
      console.log("🔄 Starting order submission...");
      
      // Показываем анимацию
      this.startSimpleAnimation();
      
      let fileUrl = '';

      // Загружаем файл только если оплата на карту
      if (this.paymentMethod === 'card') {
        try {
          const byteCharacters = atob(this.fileData);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const fileBlob = new Blob([byteArray], { type: "application/pdf" });

          const formData = new FormData();
          formData.append("file", fileBlob, this.fileName);

          const response = await api.post("/files/upload-file/", formData);
          fileUrl = response.data.file_url;
          console.log("File uploaded, URL:", fileUrl);

        } catch (e) {
          console.error("Ошибка загрузки файла:", e);
          this.showNotification("Ошибка загрузки файла", '#EF476F');
          this.isProcessing = false;
          this.closeAnimation();
          
          if (window.Telegram && window.Telegram.WebApp) {
            window.Telegram.WebApp.MainButton.hide();
            window.Telegram.WebApp.MainButton.hideProgress();
          }
          return;
        }
      }
      
      // Работаем с данными заказа и отправляем их на сервер, с учетом выбранного способа оплаты и наличия загруженного файла при оплате на карту
      try {

        // Базовые данные для любого заказа
        const baseOrderData = {
          product_name: this.productName,
          selected_size: this.selectedSize,
          payment_amount: this.paymentAmount,
          product_photo: this.productPhoto,
          payment_method: this.paymentMethod,
          batch_id: this.batchId ? Number(this.batchId) : null,
        }

        // Добавляем данные чека если есть
        if (this.fileName && fileUrl) {
          baseOrderData.file_name = this.fileName;
          baseOrderData.file_url = fileUrl;
        }

        // Добавляем данные карты если оплата картой
        if (this.paymentMethod === 'card') {
          baseOrderData.card_number = this.cardData.card_number;
          baseOrderData.card_owner = this.cardData.card_owner;
          baseOrderData.bank_name = this.cardData.bank_name;
        }

        // Drop из наличия
        if (this.isDrop && !this.isFromAbroad) {

          // Обогащаем данными для дропа
          const dropOrderData = {
            ...baseOrderData,
            profile_id: this.profileId,
            user_balance: this.userBalance,
            track_number: this.trackNumber,
            order_number: this.orderNumber,
            order_link: this.orderLink,
            delivery_service: this.deliveryService,
            baracode_photo: this.baracodePhoto,
          };

          console.log("📦 Отправляем данные заказа дроп-пользователя:", dropOrderData);

          // Отправляем запрос на создание заказа для дроп-пользователя
          const response = await api.post("/drop/profile/order/create", dropOrderData);

          console.log("✅ Ответ от сервера при создании заказа для дроп-пользователя:", response.data);

          // Ждем завершения анимации перед закрытием
          setTimeout(() => {
            if (window.Telegram && window.Telegram.WebApp) {
              
              setTimeout(() => {
                if (window.Telegram && window.Telegram.WebApp) {
                  window.Telegram.WebApp.MainButton.disable();
                  window.Telegram.WebApp.MainButton.hide();
                  window.Telegram.WebApp.MainButton.offClick(this.sendOrder);
                  window.Telegram.WebApp.close();
                }
              }, 1000);
            }
          }, 3000);

        } else if (this.isDrop && this.isFromAbroad) {
            const fromAbroadOrderData = {
              ...baseOrderData,
              profile_id: this.profileId, // добавляем только id пользователя так, как других данных нет  
            };

            console.log("📦 Отправляем данные заказа из-за рубежа дроп-пользователя:", fromAbroadOrderData)

            // Отправляем запрос на создание заказа для дроп-пользователя из-за рубежа
            const response = await api.post("/drop/profile/order/create-abroad", fromAbroadOrderData);

            console.log("✅ Ответ от сервера при создании заказа из-за рубежа для дроп-пользователя:", response.data);

            // Ждем завершения анимации перед закрытием
            setTimeout(() => {
              if (window.Telegram && window.Telegram.WebApp) {
                
                setTimeout(() => {
                  if (window.Telegram && window.Telegram.WebApp) {
                    window.Telegram.WebApp.MainButton.disable();
                    window.Telegram.WebApp.MainButton.hide();
                    window.Telegram.WebApp.MainButton.offClick(this.sendOrder);
                    window.Telegram.WebApp.close();
                  }
                }, 1000);
              }
            }, 3000);

        } else {
          const orderData = {
            productName: this.productName,
            selectedSize: this.selectedSize,
            paymentAmount: this.paymentAmount,
            fileName: this.fileName,
            fileUrl: fileUrl,
            productPhoto: this.productPhoto,
            isDrop: this.isDrop,
            paymentMethod: this.paymentMethod,
            userBalance: this.userBalance,
            profileId: this.profileId,
            deliveryService: this.deliveryService,
            orderNumber: this.orderNumber,
            trackNumber: this.trackNumber,
            baracodePhoto: this.baracodePhoto,
            orderLink: this.orderLink,
            batchId: this.batchId,
            ...(this.paymentMethod === 'card' && {
              cardNumber: this.cardData.card_number,
              cardOwner: this.cardData.card_owner,
              bankName: this.cardData.bank_name,
            })
          };

          console.log("📦 Order data for regular user:", orderData);

          // Ждем завершения анимации перед закрытием
          setTimeout(() => {
            if (window.Telegram && window.Telegram.WebApp) {
              window.Telegram.WebApp.sendData(JSON.stringify(orderData));
              window.Telegram.WebApp.close();
            }
          }, 3000);
        }
      } catch (error) {
        console.error("❌ Error creating order:", error);
        const errorMessage = error.response?.data?.detail || error.message;
        this.showNotification("Ошибка: " + errorMessage, '#EF476F');
        this.closeAnimation();
      } finally {
        this.isProcessing = false;
        
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.MainButton.hideProgress();
        }
      }
    }
  }
};
</script>

<style scoped>
.payment-container {
  padding: 16px;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%);
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding-bottom: 80px;
  border-radius: 15px;
  position: relative;
}

/* Хедер */
.payment-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.header-icon {
  font-size: 24px;
  background: linear-gradient(135deg, #2AABEE, #007AFF);
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-content {
  flex: 1;
}

.header-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  background: linear-gradient(135deg, #fff, #ccc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
}

.header-subtitle {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
}

/* Карточка с информацией о заказе */
.order-summary-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  backdrop-filter: blur(10px);
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.summary-icon {
  font-size: 18px;
  color: #2AABEE;
}

.summary-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.summary-item:last-child {
  border-bottom: none;
}

.item-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  flex-shrink: 0;
}

.item-value {
  color: white;
  font-weight: 500;
  font-size: 14px;
  text-align: right;
  margin-left: 10px;
  word-break: break-word;
}

/* Карточка суммы */
.amount-card {
  background: linear-gradient(135deg, #2AABEE, #007AFF);
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  margin-bottom: 20px;
  box-shadow: 0 6px 24px rgba(42, 171, 238, 0.2);
  position: relative;
  overflow: hidden;
}

.amount-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.amount-label {
  font-size: 13px;
  opacity: 0.9;
  margin-bottom: 6px;
}

.amount-value {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 4px;
}

/* Карточка выбора способа оплаты */
.payment-method-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px 10px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.card-header .header-icon {
  font-size: 18px;
  background: none;
  width: auto;
  height: auto;
}

.payment-options-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-option {
  background: rgba(255, 255, 255, 0.04);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: flex-start;
  gap: 14px;
  position: relative;
}

.payment-option:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.payment-option.selected {
  background: linear-gradient(135deg, 
    rgba(42, 171, 238, 0.15), 
    rgba(0, 122, 255, 0.1));
  border-color: rgba(42, 171, 238, 0.4);
  box-shadow: 0 6px 24px rgba(42, 171, 238, 0.2);
}

.option-icon {
  flex-shrink: 0;
}

.icon-circle {
  width: 44px;
  height: 44px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.balance-icon {
  background: linear-gradient(135deg, #06D6A0, #118AB2);
  box-shadow: 0 4px 12px rgba(6, 214, 160, 0.3);
}

.card-icon {
  background: linear-gradient(135deg, #FFD166, #EF9B5A);
  box-shadow: 0 4px 12px rgba(255, 209, 102, 0.3);
}

.option-content {
  flex: 1;
  min-width: 0;
}

.option-title {
  font-size: 15px;
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.option-badge {
  background: #2AABEE;
  color: white;
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.option-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 10px;
}

.balance-details {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  padding: 10px;
  margin-top: 10px;
}

.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  font-size: 13px;
}

.balance-row:first-child {
  padding-top: 0;
}

.balance-row:last-child {
  padding-bottom: 0;
}

.balance-row span:first-child {
  color: rgba(255, 255, 255, 0.6);
}

.balance-row .amount {
  color: white;
  font-weight: 600;
}

.balance-row .amount.positive {
  color: #06D6A0;
}

.balance-row .amount.zero {
  color: #EF476F;
}

.insufficient-funds {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #EF476F;
  font-size: 12px;
  font-weight: 500;
  margin-top: 6px;
  padding: 6px;
  background: rgba(239, 71, 111, 0.1);
  border-radius: 6px;
}

.warning-icon {
  font-size: 14px;
}

.option-radio {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.radio-circle {
  width: 22px;
  height: 22px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  transition: all 0.3s ease;
}

.radio-circle.selected {
  border-color: #2AABEE;
  background: #2AABEE;
  box-shadow: 0 0 0 3px rgba(42, 171, 238, 0.2);
}

.radio-circle.selected::after {
  content: '';
  display: block;
  width: 7px;
  height: 7px;
  background: white;
  border-radius: 50%;
  margin: 5.5px;
}

/* Секция банковской карты */
.bank-card-section {
  margin-bottom: 20px;
  animation: fadeIn 0.3s ease;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.section-icon {
  font-size: 18px;
  color: #2AABEE;
}

.section-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
  flex: 1;
}

.required-badge {
  background: rgba(239, 71, 111, 0.2);
  color: #EF476F;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.bank-card {
  background: linear-gradient(135deg, #2AABEE, #007AFF);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 14px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(42, 171, 238, 0.3);
  height: 200px;
  animation: slideUp 0.4s ease;
}

.card-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at top right, rgba(255,255,255,0.2) 0%, transparent 50%);
}

.card-content {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
}

.card-bank-large {
  font-size: 20px;
  font-weight: 700;
  color: white;
}

.card-chip {
  color: rgba(255, 255, 255, 0.8);
}

.card-number-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 15px 0;
}

.card-number-display {
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 20px;
  color: white;
  letter-spacing: 1.5px;
  font-weight: 500;
}

.copy-card-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  color: white;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.copy-card-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.card-owner .owner-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 3px;
}

.card-owner .owner-name {
  font-size: 16px;
  font-weight: 600;
  color: white;
}

.card-instructions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
}

.instruction-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
}

.instruction-icon {
  font-size: 14px;
  opacity: 0.8;
  flex-shrink: 0;
}

/* Секция загрузки чека */
.receipt-upload-section {
  margin-bottom: 20px;
  animation: fadeIn 0.3s ease 0.1s both;
}

.upload-area {
  background: rgba(255, 255, 255, 0.03);
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 30px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 160px;
}

.upload-area:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(42, 171, 238, 0.3);
}

.upload-area.dragover {
  background: rgba(42, 171, 238, 0.1);
  border-color: #2AABEE;
}

.upload-area.has-file {
  border-style: solid;
  border-color: #2AABEE;
  background: rgba(42, 171, 238, 0.05);
}

.upload-icon {
  margin-bottom: 12px;
  color: rgba(255, 255, 255, 0.5);
  transition: color 0.3s ease;
}

.upload-area:hover .upload-icon,
.upload-area.has-file .upload-icon {
  color: #2AABEE;
}

.upload-content {
  flex: 1;
}

.upload-title {
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin-bottom: 6px;
  word-break: break-word;
}

.upload-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 6px;
}

.upload-hint {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}

.upload-actions {
  position: absolute;
  top: 12px;
  right: 12px;
}

.remove-file-btn {
  background: rgba(239, 71, 111, 0.1);
  border: 1px solid rgba(239, 71, 111, 0.2);
  border-radius: 6px;
  color: #EF476F;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-file-btn:hover {
  background: rgba(239, 71, 111, 0.2);
}

/* Информация для пользователя */
.user-info {
  background: rgba(42, 171, 238, 0.1);
  border-radius: 12px;
  padding: 14px;
  margin-top: 20px;
  border: 1px solid rgba(42, 171, 238, 0.2);
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.info-icon {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

.info-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
}

.copy-notification {
  position: fixed;
  top: 16px;
  right: 16px;
  background: #06D6A0;
  color: white;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 500;
  font-size: 14px;
  box-shadow: 0 6px 20px rgba(6, 214, 160, 0.3);
  transform: translateX(150%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  max-width: calc(100% - 32px);
}

.copy-notification.show {
  transform: translateX(0);
}

/* Простая анимация успешной оплаты */
.simple-success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: overlayFadeIn 0.3s ease;
}

.simple-animation-container {
  width: 90%;
  max-width: 380px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 40px 24px;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: containerAppear 0.4s ease;
}

/* Зеленая галочка по центру круга */
.success-icon-container {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 30px;
}

.circle-outline {
  position: absolute;
  top: 0;
  left: 0;
  width: 120px;
  height: 120px;
  border: 3px solid #10B981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: circlePulse 2s ease-in-out infinite;
}

.checkmark {
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28);
}

.checkmark.show {
  opacity: 1;
  transform: scale(1);
}

@keyframes circlePulse {
  0%, 100% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
  50% { 
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0.1);
  }
}

@keyframes overlayFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes containerAppear {
  from { 
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Текст успеха */
.success-text {
  margin-bottom: 30px;
}

.success-title {
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 0 0 10px;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.5s ease 0.2s;
}

.success-title.fade-in {
  opacity: 1;
  transform: translateY(0);
}

.success-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.5s ease 0.4s;
}

.success-subtitle.fade-in {
  opacity: 1;
  transform: translateY(0);
}

/* Информация о заказе */
.order-info-card {
  background: rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  padding: 20px;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s ease 0.6s;
}

.order-info-card.slide-up {
  opacity: 1;
  transform: translateY(0);
}

.info-section {
  margin-bottom: 20px;
}

.info-title {
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin: 0 0 15px;
  text-align: left;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.info-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: 400;
  flex-shrink: 0;
  margin-right: 10px;
}

.info-value {
  color: white;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
  word-break: break-word;
  flex: 1;
}

.info-value.highlight {
  color: #10B981;
  font-weight: 600;
}

.status-section {
  padding-top: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 20px;
  padding: 8px 16px;
}

.status-dot {
  width: 8px;
  height: 8px;
  background: #10B981;
  border-radius: 50%;
  animation: statusDotPulse 2s ease-in-out infinite;
}

@keyframes statusDotPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.status-text {
  color: #10B981;
  font-size: 14px;
  font-weight: 500;
}

/* Анимации для существующего контента */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Адаптация для мобильных */
@media (max-width: 360px) {
  .payment-container {
    padding: 12px;
  }
  
  .header-icon {
    width: 40px;
    height: 40px;
    font-size: 22px;
  }
  
  .header-title {
    font-size: 18px;
  }
  
  .amount-value {
    font-size: 28px;
  }
  
  .icon-circle {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
  
  .bank-card {
    height: 160px;
    padding: 16px;
  }
  
  .card-number-display {
    font-size: 18px;
    letter-spacing: 1px;
  }
  
  .simple-animation-container {
    padding: 30px 20px;
  }
  
  .success-title {
    font-size: 20px;
  }
  
  .success-subtitle {
    font-size: 14px;
  }
  
  .order-info-card {
    padding: 16px;
  }
  
  .success-icon-container {
    width: 100px;
    height: 100px;
  }
  
  .circle-outline {
    width: 100px;
    height: 100px;
  }
}
</style>